/**
 * @param {MediaDeviceInfo[]} devices
 * @returns {string|undefined}
 */
export function pickPreferredQrCameraDeviceId(devices) {
	if (!devices?.length) return undefined;
	if (devices.length === 1) return devices[0].deviceId;

	const scored = devices.map((device) => {
		const label = (device.label || '').toLowerCase();
		let score = 0;
		if (/back|rear|environment|arka|traseira|trasera|зад|后置|אחור/i.test(label)) score += 10;
		if (/wide|ultra|0\.6|0,6|ultra.?wide|grand.?angle/i.test(label)) score -= 8;
		if (/front|user|selfie|前置|selfi|перед/i.test(label)) score -= 5;
		return { deviceId: device.deviceId, score };
	});

	scored.sort((a, b) => b.score - a.score);
	return scored[0]?.deviceId;
}

/**
 * @param {string|undefined} deviceId
 * @returns {MediaStreamConstraints}
 */
export function buildQrCameraConstraints(deviceId) {
	const video = {
		facingMode: { ideal: 'environment' },
		width: { ideal: 1920, min: 640 },
		height: { ideal: 1080, min: 480 },
		frameRate: { ideal: 24, max: 30 },
		focusMode: { ideal: 'continuous' },
	};

	if (deviceId) {
		video.deviceId = { exact: deviceId };
	}

	return { audio: false, video };
}

/**
 * @param {HTMLVideoElement|null|undefined} videoEl
 */
export async function applyContinuousAutofocus(videoEl) {
	const track = videoEl?.srcObject?.getVideoTracks?.()?.[0];
	if (!track?.applyConstraints) return;

	try {
		const capabilities = track.getCapabilities?.() || {};
		if (capabilities.focusMode?.includes('continuous')) {
			await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] });
			return;
		}
		if (capabilities.focusMode?.includes('auto')) {
			await track.applyConstraints({ advanced: [{ focusMode: 'auto' }] });
		}
	} catch (_) {
		try {
			await track.applyConstraints({ focusMode: 'continuous' });
		} catch (err) {
			// Browser may not expose focus controls for this camera.
		}
	}
}

/**
 * @param {HTMLVideoElement|null|undefined} videoEl
 */
export async function refocusCamera(videoEl) {
	const track = videoEl?.srcObject?.getVideoTracks?.()?.[0];
	if (!track?.applyConstraints) return;

	try {
		const capabilities = track.getCapabilities?.() || {};
		if (capabilities.focusMode?.includes('single-shot')) {
			await track.applyConstraints({ advanced: [{ focusMode: 'single-shot' }] });
			return;
		}
		if (capabilities.focusMode?.includes('continuous')) {
			await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] });
		}
	} catch (_) {
		try {
			await track.applyConstraints({ focusMode: 'single-shot' });
		} catch (err) {
			await applyContinuousAutofocus(videoEl);
		}
	}
}

/**
 * @returns {Promise<{ BrowserQRCodeReader: typeof import('@zxing/browser').BrowserQRCodeReader, hints: Map<any, any> }>}
 */
export async function createQrCodeReader() {
	const [{ BrowserQRCodeReader }, { DecodeHintType }] = await Promise.all([
		import('@zxing/browser'),
		import('@zxing/library'),
	]);
	const hints = new Map();
	hints.set(DecodeHintType.TRY_HARDER, true);
	hints.set(DecodeHintType.ASSUME_GS1, false);
	const reader = new BrowserQRCodeReader(hints, {
		delayBetweenScanAttempts: 80,
		delayBetweenScanSuccess: 400,
	});
	return { reader };
}

/**
 * @param {import('@zxing/browser').BrowserQRCodeReader} reader
 * @param {HTMLVideoElement} videoEl
 * @param {(result: import('@zxing/library').Result) => void} onResult
 */
export async function startQrVideoScan(reader, videoEl, onResult) {
	const { BrowserQRCodeReader } = await import('@zxing/browser');
	const devices = await BrowserQRCodeReader.listVideoInputDevices();
	const deviceId = pickPreferredQrCameraDeviceId(devices);
	const constraints = buildQrCameraConstraints(deviceId);

	try {
		const controls = await reader.decodeFromConstraints(constraints, videoEl, (result) => {
			if (result) onResult(result);
		});
		await applyContinuousAutofocus(videoEl);
		return controls;
	} catch (err) {
		const fallbackControls = await reader.decodeFromVideoDevice(deviceId, videoEl, (result) => {
			if (result) onResult(result);
		});
		await applyContinuousAutofocus(videoEl);
		return fallbackControls;
	}
}
